import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { CARD_MIN_WIDTH } from 'scenes/ProjectBank/constants/bankLayout';

/** The user's "reduce motion" setting, as a media query. */
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * The media fragment of the still frame a thumbnail shows with reduced
 * motion. iOS ignores `preload` and decodes no frame of a video that does
 * not play unless the URL asks for a moment of it. A fragment never leaves
 * the browser, so the signature of the URL stays valid.
 */
export const STILL_FRAME_FRAGMENT = '#t=0.1';

/**
 * How far beyond its scroll area a thumbnail already counts as on screen:
 * about one card (cards are square, CARD_MIN_WIDTH wide or wider from sm up
 * and a little narrower on a phone). A card scrolls in with its video
 * already loading, and one scrolled a card's height away gives its player
 * back.
 */
export const IN_VIEW_ROOT_MARGIN = `${CARD_MIN_WIDTH}px 0px`;

// The bank's scroll container (BankContentPane). The cards scroll inside it,
// and a root margin reaches only beyond the observer's root, so it is the
// root; outside one the viewport is.
const SCROLL_AREA_SELECTOR = '[data-bank-scroll-area]';

// play() returns a promise (older browsers return nothing) that rejects when
// the browser will not autoplay (iOS in Low Power Mode) or when a pause() or
// the release of the player cuts the start short. Either way the thumbnail
// just stands still, so the rejection is swallowed.
const playQuietly = (video) => {
  const playing = video.play();
  if (playing && typeof playing.catch === 'function') {
    playing.catch(() => {});
  }
};

// Gives the element's media player back. Chrome on Android allows a page a
// few dozen of them and a paused video keeps its own, so scrolling through a
// big folder would use them up and leave the later thumbnails failing.
// Removing the src alone keeps the player; load() without one lets it go.
// An element without a src has nothing to give back.
const releasePlayer = (video) => {
  if (!video.hasAttribute('src')) return;
  video.pause();
  video.removeAttribute('src');
  video.load();
};

/**
 * The looping animated thumbnail of a public video: the short .webm the
 * backend renders of every uploaded video, played as the gallery's
 * VideoPreviewComponent plays it (muted, looping, inline, no controls). It
 * is decorative: hidden from assistive tech, not draggable, and without the
 * picture-in-picture and cast buttons browsers put on a video. The caller's
 * class sizes it (object-fit cover, like a photo thumbnail).
 *
 * A folder can show a hundred of them at once, mostly on a phone, so only a
 * thumbnail on screen (or a card's height from it, IN_VIEW_ROOT_MARGIN) has
 * its URL and a media player: an IntersectionObserver attaches the URL and
 * plays it as it comes, and gives the player back as it goes, attaching the
 * same URL again when it returns. A browser without an observer plays it
 * straight away, as the gallery does. With `prefers-reduced-motion: reduce`
 * it never plays but shows the frame at STILL_FRAME_FRAGMENT; the setting may
 * change while the page is open. The `<video>` carries the URL itself (no
 * `<source>` child), so a broken or expired URL fires `onError` on it, where
 * useBankThumbnail's handler asks for the URL again or falls back to the
 * format glyph; an element emptied on purpose reports no error.
 * @param {{src: string, onError?: Function, className?: string}} props
 */
export function BankVideoThumbnail({ src, onError, className }) {
  const videoRef = useRef(null);
  const reduceMotion = useMediaQuery(REDUCED_MOTION_QUERY, { noSsr: true });
  // Read by the observer's callback and by the setting's changes below.
  const reduceMotionRef = useRef(reduceMotion);
  const onScreenRef = useRef(false);

  // The element's own set-up and tear-down. React 16 sets `muted` as the
  // property only; the attribute as well is insurance for iOS, which plays a
  // video without a tap only when it is muted. On unmount the player is given
  // back, through the element captured here: the ref is null by then.
  useEffect(() => {
    const video = videoRef.current;
    video.defaultMuted = true;
    video.muted = true;
    return () => releasePlayer(video);
  }, []);

  // Near the screen the thumbnail takes its URL and plays (or, with reduced
  // motion, shows its still frame); away from it, it gives its player back.
  // Without an observer it plays straight away. A new URL starts from a
  // released player.
  useEffect(() => {
    const video = videoRef.current;
    releasePlayer(video);
    const show = () => {
      onScreenRef.current = true;
      if (!video.hasAttribute('src')) {
        video.setAttribute(
          'src',
          reduceMotionRef.current ? `${src}${STILL_FRAME_FRAGMENT}` : src
        );
      }
      if (!reduceMotionRef.current) playQuietly(video);
    };
    const hide = () => {
      onScreenRef.current = false;
      releasePlayer(video);
    };

    const Observer = window.IntersectionObserver;
    if (typeof Observer !== 'function') {
      show();
      return undefined;
    }
    // Entries already queued when the observer is disconnected may still be
    // delivered (the spec keeps them; WebKit has been seen to), and one must
    // not hand a URL and a player to a thumbnail that is gone or has moved on
    // to another URL.
    let live = true;
    const observer = new Observer(
      (entries) => {
        if (!live) return;
        // The newest entry tells where the thumbnail is now.
        if (entries[entries.length - 1].isIntersecting) {
          show();
        } else {
          hide();
        }
      },
      {
        root: video.closest(SCROLL_AREA_SELECTOR),
        rootMargin: IN_VIEW_ROOT_MARGIN,
      }
    );
    observer.observe(video);
    return () => {
      live = false;
      observer.disconnect();
    };
  }, [src]);

  // Reduced motion switched on stops the thumbnail on the frame it shows;
  // switched off, it plays again if it is on screen.
  useEffect(() => {
    if (reduceMotionRef.current === reduceMotion) return;
    reduceMotionRef.current = reduceMotion;
    const video = videoRef.current;
    if (reduceMotion) {
      video.pause();
    } else if (onScreenRef.current) {
      playQuietly(video);
    }
  }, [reduceMotion]);

  // An element emptied on purpose (its player given back) has no URL that
  // could have failed.
  const handleError = (event) => {
    if (!event.currentTarget.hasAttribute('src')) return;
    if (onError) onError(event);
  };

  return (
    <video
      ref={videoRef}
      className={className}
      muted
      loop
      playsInline
      preload="metadata"
      disablePictureInPicture
      // React 16 does not know disableRemotePlayback yet; spelled as the
      // attribute, it keeps Chrome on Android from painting a cast button on
      // every card while a cast device is in reach.
      disableremoteplayback=""
      aria-hidden="true"
      draggable={false}
      onError={handleError}
    />
  );
}

BankVideoThumbnail.propTypes = {
  /** Signed URL of the video's .webm thumbnail. */
  src: PropTypes.string.isRequired,
  /** useBankThumbnail's `onError`: the URL is broken or has expired. */
  onError: PropTypes.func,
  className: PropTypes.string,
};

export default BankVideoThumbnail;
